// eslint-disable-next-line import/no-extraneous-dependencies
import { cn } from "@/lib/utils";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";

const thumbVariant = cva("", {
    variants: {
        variant: {
            default: "relative flex-1 rounded-full bg-gray-400",
            ghost: "bg-transparent",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

const ScrollBar = React.forwardRef<
    React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
    React.ComponentPropsWithoutRef<
        typeof ScrollAreaPrimitive.ScrollAreaScrollbar
    > &
        VariantProps<typeof thumbVariant>
>(({ className, variant, orientation = "vertical", ...props }, ref) => (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
        ref={ref}
        className={cn(
            "flex touch-none select-none transition-colors",
            orientation === "vertical" &&
                "h-full w-2.5 border-l border-l-transparent p-[1px]",
            orientation === "horizontal" &&
                "h-2.5 flex-col border-t border-t-transparent p-[1px]",
            className,
        )}
        orientation={orientation}
        {...props}
    >
        <ScrollAreaPrimitive.ScrollAreaThumb
            className={cn(thumbVariant({ variant }), "scrollbar-thumb")}
        />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

interface ScrollAreaProps extends React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.Root
> {
    scrollRef?: React.Ref<HTMLDivElement>;
    viewportClassName?: string;
    viewportProps?: React.HTMLAttributes<HTMLDivElement>;
}

const ScrollArea = React.forwardRef<
    React.ElementRef<typeof ScrollAreaPrimitive.Root>,
    ScrollAreaProps & VariantProps<typeof thumbVariant>
>(
    (
        {
            className,
            children,
            onScroll,
            scrollRef,
            variant,
            viewportClassName,
            viewportProps,
            ...props
        },
        ref,
    ) => (
        <ScrollAreaPrimitive.Root
            ref={ref}
            className={cn("relative overflow-hidden", className)}
            {...props}
        >
            <ScrollAreaPrimitive.Viewport
                ref={scrollRef}
                className={cn(
                    "h-full w-full rounded-[inherit] [&_>div]:h-full",
                    viewportClassName,
                )}
                onScroll={onScroll}
                {...viewportProps}
            >
                {children}
            </ScrollAreaPrimitive.Viewport>
            <ScrollBar variant={variant} />
            <ScrollAreaPrimitive.Corner />
        </ScrollAreaPrimitive.Root>
    ),
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

export { ScrollArea, ScrollBar };
